/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IHeaderViewProps } from '@msdyn365-commerce-modules/header';
import { IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';
import {
    IS_LOCAL_DEVELOPMENT,
    getOnlineChannelInfoTimer,
    getToggleLang,
    setOnlineChannelInfoInCache
} from '../../../utilities/language-change';
import { getMOEOnlineStoresAsync } from '../../../actions/extensions/DataActionExtension.g';
import {
    ENGLISH_LANG_CODE,
    ENGLISH_TOGGLE_PREFIX,
    MAORI_LANG_CODE,
    MAORI_TOGGLE_PREFIX,
    AXE_MAORI_LANG_CODE
} from '../../../utilities/constants';
import MsDyn365 from '@msdyn365-commerce/core';

const headerView: React.FC<IHeaderViewProps & IHeaderProps<{}>> = (props: IHeaderViewProps & IHeaderProps<{}>) => {
    const {
        HeaderTag,
        HeaderContainer,
        HeaderTopBarContainer,
        config,
        MobileMenuContainer,
        MobileMenuBodyContainer,
        MobileMenuLinksContainer,
        Divider,
        context
    } = props;
    const { headerText, headerTextLink } = config;
    const toggleLang = getToggleLang(context);
    const toggleTooltip = toggleLang === 'maori' ? 'Whakaatuhia te paetukutuku i te reo Ingarihi' : 'Display website in Te Reo Māori';
    const hostname = context.request.url?.requestUrl?.hostname;

    // bug 102348: Effect for changing document lang code to Maori
    React.useEffect(() => {
        const htmlElement: HTMLElement | null = document.querySelector('html');
        if (htmlElement) {
            htmlElement.lang = AXE_MAORI_LANG_CODE;
            htmlElement.translate = false;
        }
    }, []);

    React.useEffect(() => {
        if (IS_LOCAL_DEVELOPMENT) {
            return;
        }
        if (MsDyn365.isBrowser && !localStorage.getItem(`moeOnlineChannelInfo${hostname}`)) {
            void getMOEOnlineStoresAsync({ callerContext: props.context.actionContext, bypassCache: 'get' }).then(channelResponse => {
                setOnlineChannelInfoInCache(context, JSON.stringify(channelResponse));
            });
        } else {
            const hours = 24;
            const setupTime = getOnlineChannelInfoTimer(context);
            const now = new Date().getTime();

            if (MsDyn365.isBrowser && now - setupTime > hours * 60 * 60 * 1000) {
                localStorage.removeItem('moeOnlineChannelInfoTimer');
                void getMOEOnlineStoresAsync({ callerContext: props.context.actionContext, bypassCache: 'get' }).then(channelResponse => {
                    setOnlineChannelInfoInCache(context, JSON.stringify(channelResponse));
                });
            }
        }
    }, []);

    const _getSiteRedirectionUrl = () => {
        const { requestUrl } = props.context.request.url;
        const langCode = context.request.locale.toLowerCase();

        let pathUrl = requestUrl.pathname;
        if (langCode === ENGLISH_LANG_CODE) {
            pathUrl = MAORI_TOGGLE_PREFIX + pathUrl;
        } else if (langCode === MAORI_LANG_CODE) {
            pathUrl = pathUrl.replace(ENGLISH_TOGGLE_PREFIX, '');
        }
        const redirectUrl = `${requestUrl.origin}/${pathUrl}${requestUrl.search}`;
        return redirectUrl;
    };

    return (
        <Module {...HeaderTag}>
            <Node {...HeaderContainer}>
                <Node {...HeaderTopBarContainer}>
                    {/*--for Desktop UI--*/}
                    <div className='moe-header__desktop moe-header__desktop--och'>
                        <div className='moe-header__desktop-container'>
                            <div className='moe-header__desktop-container-left'>
                                <div className='moe-header__logo-wrap och-header__logo-wrap'>
                                    {props.logo}
                                    {/*<h2 className='moe-header__logo-txt och-header__logo-txt'>{headerText}</h2>*/}
                                    <div className='moe-header__logo-txt-column'>
                                        <a
                                            className='moe-header__logo-txt och-header__logo-txt'
                                            href={headerTextLink?.linkUrl.destinationUrl}
                                        >
                                            {headerText}
                                        </a>
                                    </div>
                                </div>
                            </div>
                            <div className='moe-header__desktop-container-right'>
                                <div className='moe-header-actions'>{props.navIcon}</div>
                            </div>
                        </div>
                    </div>

                    {/*--for Tablet and Mobile Responsive UI--*/}
                    <div className='moe-header__mobile moe-header__mobile--och'>
                        <div className='moe-header__mobile-container'>
                            <div className='moe-header__mobile-container-left'>
                                {props.logo}
                                <a className='moe-header__logo-txt och-header__logo-txt' href={headerTextLink?.linkUrl.destinationUrl}>
                                    {headerText}
                                </a>
                            </div>
                            <div className='moe-header__mobile-container-right'>
                                <div className='moe-header-actions'>{props.navIcon}</div>
                            </div>
                        </div>
                    </div>
                    {_renderReactFragment(props.search)}
                    {props.preferredStore}
                    {_renderDesktopAccountBlock(props)}
                    {props.wishListIconDesktop}
                    <Node {...Divider} />
                    {props.cartIcon}
                    {_renderReactFragment(props.siteOptions)}
                </Node>
                <Node {...MobileMenuContainer}>
                    <Node {...MobileMenuBodyContainer}>
                        {props.MobileMenuHeader}
                        <div className='moe-language-toggle-holder--mobile'>
                            <a href={_getSiteRedirectionUrl()} className='moe-btn-language-toggle' title={toggleTooltip} role='buttton'>
                                <ul className='moe-btn-language-toggle__list'>
                                    <li className={`moe-btn-language-toggle__list-item ${toggleLang === 'maori' ? 'active' : 'inactive'}`}>
                                        <span>Reo Māori</span>
                                    </li>
                                    <li className={`moe-btn-language-toggle__list-item ${toggleLang === 'maori' ? 'inactive' : 'active'}`}>
                                        <span>English</span>
                                    </li>
                                </ul>
                            </a>
                        </div>
                        {_renderReactFragment(props.menuBar)}
                        <Node {...MobileMenuLinksContainer}>
                            {props.accountLinks ? props.accountLinks.map(link => link) : false}
                            {props.siteOptions}
                            {props.wishListIconMobile}
                            {props.signInLink}
                            {props.signOutLink}
                        </Node>
                    </Node>
                </Node>
                {_renderReactFragment(props.menuBar)}
                {/* Language toggle button
                only 1 list item will be visible at a time
                active li becomes visible text
                inactive li becomes the round dot */}
                <div className='moe-language-toggle-holder--desktop'>
                    <a href={_getSiteRedirectionUrl()} className='moe-btn-language-toggle' title={toggleTooltip} role='button'>
                        <ul className='moe-btn-language-toggle__list'>
                            <li className={`moe-btn-language-toggle__list-item ${toggleLang === 'maori' ? 'active' : 'inactive'}`}>
                                <span>Reo Māori</span>
                            </li>
                            <li className={`moe-btn-language-toggle__list-item ${toggleLang === 'maori' ? 'inactive' : 'active'}`}>
                                <span>English</span>
                            </li>
                        </ul>
                    </a>
                </div>
                {/* future buttons can come here */}
            </Node>
        </Module>
    );
};

function _renderDesktopAccountBlock(props: IHeaderViewProps): JSX.Element | null {
    const {
        AccountInfoDropdownParentContainer,
        AccountInfoDropdownPopoverConentContainer,
        accountInfoDropdownButton,
        signOutLink,
        signInLink,
        accountLinks
    } = props;

    if (AccountInfoDropdownParentContainer) {
        if (AccountInfoDropdownPopoverConentContainer) {
            return (
                <Node {...AccountInfoDropdownParentContainer}>
                    {accountInfoDropdownButton}
                    <Node {...AccountInfoDropdownPopoverConentContainer}>
                        {accountLinks ? accountLinks.map(link => link) : false}
                        {signOutLink}
                    </Node>
                </Node>
            );
        } else if (signInLink) {
            return <Node {...AccountInfoDropdownParentContainer}>{signInLink}</Node>;
        }
    }
    props.context.telemetry.error('Header content is empty, module wont render.');
    return null;
}

function _renderReactFragment(items: React.ReactNode[]): JSX.Element | null {
    return (
        <>
            {items && items.length > 0
                ? items.map((slot: React.ReactNode, index: number) => {
                      return <React.Fragment key={index}>{slot}</React.Fragment>;
                  })
                : null}
        </>
    );
}

export default headerView;
